import { useState } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleAlert } from "lucide-react";
import { toast } from "sonner";
import { chipToast } from "@/components/ui/chip-toast";
import { disableMfa } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "./profile-form";

export function ResetTwoFactorDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [password, setPassword] = useState("");

  const resetMutation = useMutation({
    mutationFn: disableMfa,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["auth", "mfa-status"] });
      chipToast({
        message: t`Two-factor authentication reset. Set up a new 2FA method at your next sign-in.`,
      });
      setPassword("");
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : t`Failed to reset 2FA`,
      );
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setPassword("");
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-175">
        <DialogHeader>
          <DialogTitle><Trans>Reset two-factor authentication</Trans></DialogTitle>
          <DialogDescription>
            <Trans>
              Remove your current 2FA method and set it up again on your next
              sign-in.
            </Trans>
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 rounded-lg bg-(--color-warning-bg) p-3">
            <CircleAlert className="mt-0.5 size-5 shrink-0 text-(--color-warning-text)" />
            <div>
              <p className="text-sm font-medium text-(--color-warning-text)">
                <Trans>Please note</Trans>
              </p>
              <p className="text-sm text-(--color-text-secondary)">
                <Trans>
                  You&rsquo;ll need to verify your identity on the next step
                  before setting up a new authenticator, SMS or backup method.
                </Trans>
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-(--color-border) p-4">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-(--color-text-primary)">
                <Trans>Current method</Trans>
              </span>
              <span className="inline-flex items-center rounded-full bg-(--color-success-bg) px-2.5 py-0.5 text-xs font-medium text-(--color-success-text)">
                <Trans>Active</Trans>
              </span>
            </div>
            <p className="mt-1 text-sm text-(--color-text-secondary)">
              <Trans>SMS authentication • Enabled on Feb 20, 2026</Trans>
            </p>
          </div>

          <form
            id="reset-2fa-form"
            onSubmit={(e) => {
              e.preventDefault();
              resetMutation.mutate();
            }}
          >
            <Field label={t`Confirm password`} htmlFor="reset-2fa-password">
              <Input
                id="reset-2fa-password"
                type="password"
                autoComplete="current-password"
                placeholder={t`Enter password to continue…`}
                className="h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          </form>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={resetMutation.isPending}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            type="submit"
            form="reset-2fa-form"
            disabled={resetMutation.isPending || password.length === 0}
          >
            {resetMutation.isPending ? t`Resetting…` : t`Reset 2FA`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
